import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../components/AuthProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { LogOut, Globe, LayoutDashboard, Upload, Trash2, Edit3, Save, Plus, X, AlertTriangle, CheckCircle2, Loader2, Link as LinkIcon } from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';
import { UpdateProfileForm, ChangePasswordForm } from '../components/AccountSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'hiring-onboarding', label: 'Hiring & Onboarding' },
  { id: 'hr-policies', label: 'HR Policies' },
  { id: 'performance-development', label: 'Performance & Development' },
  { id: 'benefits-compensation', label: 'Benefits & Compensation' },
  { id: 'termination-offboarding', label: 'Termination & Offboarding' },
  { id: 'compliance-legal', label: 'Compliance & Legal' },
  { id: 'culture-dei-engagement', label: 'Culture, DEI & Engagement' },
  { id: 'hr-operations', label: 'HR Operations' },
];

const Tab = ({ active, onClick, children, testid, badgeCount }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
  >
    {children}
    {badgeCount > 0 && (
      <span
        className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold inline-flex items-center justify-center shadow ring-2 ring-white"
        data-testid={`${testid}-badge`}
        aria-label={`${badgeCount} unread`}
      >
        {badgeCount > 99 ? '99+' : badgeCount}
      </span>
    )}
  </button>
);

const TemplatesTab = ({ authHeader }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(null);
  // bulk upload form
  const [uploadCategory, setUploadCategory] = useState(CATEGORIES[0].id);
  const [uploadTier, setUploadTier] = useState('full_230');
  const [uploadFiles, setUploadFiles] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (filterCat) params.set('category', filterCat);
      if (filterTier) params.set('tier_access', filterTier);
      const { data } = await axios.get(`${API}/admin/templates?${params}`, { headers: authHeader() });
      setTemplates(data.templates);
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles || uploadFiles.length === 0) {
      setFlash({ type: 'error', msg: 'Select at least one file.' });
      return;
    }
    setBusy(true);
    setFlash(null);
    try {
      const fd = new FormData();
      Array.from(uploadFiles).forEach((f) => fd.append('files', f));
      fd.append('category', uploadCategory);
      fd.append('tier_access', uploadTier);
      const { data } = await axios.post(`${API}/admin/templates/bulk-upload`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const errs = data.errors?.length ? ` (${data.errors.length} skipped)` : '';
      setFlash({ type: 'success', msg: `Uploaded ${data.created_count} template${data.created_count === 1 ? '' : 's'}${errs}.` });
      setUploadFiles(null);
      // reset the actual file input
      const input = document.getElementById('admin-bulk-files');
      if (input) input.value = '';
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handlePurgeOrphans = async () => {
    if (!window.confirm('This will delete every template DB row that has no storage_path (leftovers from before object storage). Files already in object storage are NOT touched. Continue?')) return;
    setBusy(true);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API}/admin/templates/purge-orphans`, {}, { headers: authHeader() });
      setFlash({ type: 'success', msg: `Purged ${data.deleted} orphan template record${data.deleted === 1 ? '' : 's'}.` });
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleApplyCore50 = async () => {
    if (!window.confirm('This will reset every active template to full_230, then tag the 50 preset essentials as core_50. Safe to re-run. Continue?')) return;
    setBusy(true);
    setFlash(null);
    try {
      const { data } = await axios.post(`${API}/admin/templates/apply-core-50-preset`, {}, { headers: authHeader() });
      const c = data.final_counts || {};
      const unmatchedCount = (data.unmatched || []).length;
      const msg =
        unmatchedCount === 0
          ? `Tagged ${data.tagged_as_core_50}/${data.expected_core_50} as core_50. Final: core_50=${c.core_50}, full_230=${c.full_230}, total=${c.total}.`
          : `Tagged ${data.tagged_as_core_50}/${data.expected_core_50} as core_50 (${unmatchedCount} unmatched — see browser console). Final: core_50=${c.core_50}, full_230=${c.full_230}, total=${c.total}.`;
      if (unmatchedCount > 0) {
        // eslint-disable-next-line no-console
        console.warn('[core_50 unmatched]', data.unmatched);
      }
      setFlash({ type: unmatchedCount === 0 ? 'success' : 'error', msg });
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template permanently? File will be removed from storage.')) return;
    try {
      await axios.delete(`${API}/admin/templates/${id}`, { headers: authHeader() });
      setFlash({ type: 'success', msg: 'Template deleted.' });
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/admin/templates/${editing.id}`, {
        name: editing.name,
        description: editing.description,
        category: editing.category,
        tier_access: editing.tier_access,
        active: editing.active,
      }, { headers: authHeader() });
      setEditing(null);
      setFlash({ type: 'success', msg: 'Template updated.' });
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Bulk upload */}
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="admin-bulk-upload">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Bulk upload templates</h2>
        <p className="text-sm text-gray-500 mb-4">Pick multiple files at once; they will all be tagged with the same category and tier. Template name is derived from the filename — edit individually below if needed. Max 25 MB / file.</p>
        <form onSubmit={handleBulkUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="upload-category">Category</Label>
              <select id="upload-category" data-testid="upload-category" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="upload-tier">Tier access</Label>
              <select id="upload-tier" data-testid="upload-tier" value={uploadTier} onChange={(e) => setUploadTier(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm">
                <option value="core_50">core_50 (Standard + Pro + Elite)</option>
                <option value="full_230">full_230 (Pro + Elite only)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="admin-bulk-files">Files (.pdf .doc .docx .xlsx .pptx .txt)</Label>
              <input id="admin-bulk-files" data-testid="admin-bulk-files" type="file" multiple onChange={(e) => setUploadFiles(e.target.files)} className="w-full h-10 text-sm" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-upload-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
          </Button>
        </form>

        {/* One-shot migration + tier-preset admin actions */}
        <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">Maintenance actions</h4>
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={handlePurgeOrphans}
              data-testid="admin-purge-orphans"
              className="text-sm"
            >
              Purge orphan filesystem records
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={handleApplyCore50}
              data-testid="admin-apply-core50"
              className="text-sm"
            >
              Apply core_50 preset (tag 50 essentials)
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Purge removes DB rows that have no <code>storage_path</code> (leftovers from the pre-object-storage era). Apply-core_50 resets every active template to <code>full_230</code>, then re-tags the 50 preset essentials as <code>core_50</code>. Both are safe to re-run.
          </p>
        </div>
      </section>

      {flash && (
        <div className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="admin-flash">
          {flash.type === 'error' ? <AlertTriangle className="w-4 h-4 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 mt-0.5" />}
          <span>{flash.msg}</span>
        </div>
      )}

      {/* Templates list */}
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="adm-q">Search</Label>
            <Input id="adm-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name..." data-testid="admin-search" />
          </div>
          <div>
            <Label htmlFor="adm-cat">Category</Label>
            <select id="adm-cat" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm">
              <option value="">All</option>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="adm-tier">Tier</Label>
            <select id="adm-tier" value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm">
              <option value="">All</option>
              <option value="core_50">core_50</option>
              <option value="full_230">full_230</option>
            </select>
          </div>
          <Button onClick={load} variant="outline" data-testid="admin-search-btn">Search</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Category</th>
                <th className="p-3">Tier</th>
                <th className="p-3">File</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-6 text-center text-gray-400">Loading...</td></tr>
              ) : templates.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-gray-400">No templates yet. Upload some above to get started.</td></tr>
              ) : templates.map((t) => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{t.name}</div>
                    {t.description && <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>}
                  </td>
                  <td className="p-3 text-gray-700">{CATEGORIES.find((c) => c.id === t.category)?.label || t.category}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.tier_access === 'core_50' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{t.tier_access}</span>
                  </td>
                  <td className="p-3 text-xs text-gray-500">{t.file_name}<br />{(t.file_size / 1024).toFixed(0)} KB</td>
                  <td className="p-3 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...t })} data-testid={`admin-edit-${t.id}`}><Edit3 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-red-600 hover:text-red-700" data-testid={`admin-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Edit template</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm">
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Tier access</Label>
                  <select value={editing.tier_access} onChange={(e) => setEditing({ ...editing, tier_access: e.target.value })} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm">
                    <option value="core_50">core_50</option>
                    <option value="full_230">full_230</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Active (visible to members)
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="admin-save-edit"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RemindersTab = ({ authHeader }) => {
  const [data, setData] = useState({ title: '', month_label: '', items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [newText, setNewText] = useState('');
  const [newLink, setNewLink] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await axios.get(`${API}/portal/content-modules`);
        setData(c.monthly_reminders);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setFlash(null);
    try {
      const cleanItems = data.items.map(({ text, link }) => ({ text, link: (link && link.trim()) || null }));
      await axios.put(`${API}/portal/content-modules/monthly-reminders`, { ...data, items: cleanItems }, { headers: authHeader() });
      setFlash({ type: 'success', msg: 'Reminders saved.' });
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  const updateItem = (i, patch) => {
    const next = [...data.items];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, items: next });
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="admin-reminders">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Monthly HR Reminders</h2>
      <p className="text-sm text-gray-500 mb-4">Visible to Pro and Elite members on their dashboard. Add an optional link to a source article or related template — clicking the reminder will open it in a new tab.</p>
      <div className="space-y-3">
        <div><Label>Title</Label><Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="rem-title" /></div>
        <div><Label>Month label (e.g., June 2026)</Label><Input value={data.month_label} onChange={(e) => setData({ ...data, month_label: e.target.value })} data-testid="rem-month" /></div>
        <div>
          <Label>Reminder items</Label>
          <ul className="space-y-3 mt-2">
            {data.items.map((item, i) => (
              <li key={i} className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid={`rem-item-${i}`}>
                <div className="flex gap-2">
                  <Input
                    placeholder="Reminder text..."
                    value={item.text}
                    onChange={(e) => updateItem(i, { text: e.target.value })}
                    data-testid={`rem-item-text-${i}`}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setData({ ...data, items: data.items.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
                <Input
                  type="url"
                  placeholder="Optional link (https://...) — leave blank for plain text"
                  value={item.link || ''}
                  onChange={(e) => updateItem(i, { link: e.target.value })}
                  data-testid={`rem-item-link-${i}`}
                />
              </li>
            ))}
          </ul>
          <div className="mt-4 p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 space-y-2">
            <Input placeholder="New reminder text..." value={newText} onChange={(e) => setNewText(e.target.value)} data-testid="rem-new-text" />
            <Input type="url" placeholder="Optional link (https://...)" value={newLink} onChange={(e) => setNewLink(e.target.value)} data-testid="rem-new-link" />
            <Button
              onClick={() => {
                if (!newText.trim()) return;
                setData({ ...data, items: [...data.items, { text: newText.trim(), link: newLink.trim() || null }] });
                setNewText(''); setNewLink('');
              }}
              data-testid="rem-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />Add reminder
            </Button>
          </div>
        </div>
      </div>
      {flash && (
        <div className={`mt-4 p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
      )}
      <div className="mt-5">
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-reminders-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save reminders</>}
        </Button>
      </div>
    </section>
  );
};

// Reusable template picker (loads all templates once and offers a <select>).
const useTemplateOptions = (authHeader) => {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/templates?limit=500`, { headers: authHeader() });
        setTemplates(data.templates || []);
      } catch (e) { /* admin sees empty dropdown */ }
    })();
  }, [authHeader]);
  return templates;
};

const TemplatePickerSelect = ({ value, onChange, templates, testid }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value || null)}
    className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white"
    data-testid={testid}
  >
    <option value="">— No linked template (use Optional link instead) —</option>
    {templates.map((t) => (
      <option key={t.id} value={t.id}>{t.name}</option>
    ))}
  </select>
);

const FeaturedToolsTab = ({ authHeader }) => {
  const [data, setData] = useState({ title: '', items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const templates = useTemplateOptions(authHeader);

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await axios.get(`${API}/portal/content-modules`);
        setData(c.featured_tools);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setFlash(null);
    try {
      await axios.put(`${API}/portal/content-modules/featured-tools`, data, { headers: authHeader() });
      setFlash({ type: 'success', msg: 'Featured tools saved.' });
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  const updateItem = (i, patch) => {
    const next = [...data.items];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, items: next });
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="admin-tools">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Featured HR Tools</h2>
      <p className="text-sm text-gray-500 mb-4">Spotlight 2–3 rotating high-value resources. Each tile is clickable on the member dashboard — choose a linked template (preferred) or enter a custom URL.</p>
      <div className="space-y-4">
        <div><Label>Section title</Label><Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} /></div>
        {data.items.map((item, i) => (
          <div key={item.id || i} className="rounded-lg border border-gray-200 p-4 space-y-2" data-testid={`tool-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Tool #{i + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => setData({ ...data, items: data.items.filter((_, j) => j !== i) })}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
            <Input placeholder="Title" value={item.title} onChange={(e) => updateItem(i, { title: e.target.value })} data-testid={`tool-title-${i}`} />
            <Textarea rows={2} placeholder="Description" value={item.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
            <div>
              <Label className="text-xs text-gray-500">Linked template (preferred)</Label>
              <TemplatePickerSelect
                value={item.template_id}
                onChange={(v) => updateItem(i, { template_id: v })}
                templates={templates}
                testid={`tool-template-${i}`}
              />
            </div>
            <Input placeholder="Optional link (used only if no template is selected)" value={item.link || ''} onChange={(e) => updateItem(i, { link: e.target.value })} />
          </div>
        ))}
        <Button variant="outline" onClick={() => setData({ ...data, items: [...data.items, { id: `tool-${Date.now()}`, title: '', description: '', link: '', template_id: null }] })}>
          <Plus className="w-4 h-4 mr-2" />Add tool
        </Button>
      </div>
      {flash && (
        <div className={`mt-4 p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
      )}
      <div className="mt-5">
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-tools-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save featured tools</>}
        </Button>
      </div>
    </section>
  );
};

const StarterEssentialsTab = ({ authHeader }) => {
  const [data, setData] = useState({ title: '', items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const templates = useTemplateOptions(authHeader);

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await axios.get(`${API}/portal/content-modules`);
        setData(c.starter_essentials);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setFlash(null);
    try {
      await axios.put(`${API}/portal/content-modules/starter-essentials`, data, { headers: authHeader() });
      setFlash({ type: 'success', msg: 'Starter essentials saved.' });
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  const updateItem = (i, patch) => {
    const next = [...data.items];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, items: next });
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="admin-starter">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Starter Essentials</h2>
      <p className="text-sm text-gray-500 mb-4">The &ldquo;New to DriveHR?&rdquo; section on the member dashboard. Pick the templates new members should grab first.</p>
      <div className="space-y-4">
        <div><Label>Section title</Label><Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="starter-title" /></div>
        {data.items.map((item, i) => (
          <div key={item.id || i} className="rounded-lg border border-gray-200 p-4 space-y-2" data-testid={`starter-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Essential #{i + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => setData({ ...data, items: data.items.filter((_, j) => j !== i) })}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
            <Input placeholder="Display title (e.g., New Hire Onboarding Checklist)" value={item.title} onChange={(e) => updateItem(i, { title: e.target.value })} data-testid={`starter-title-input-${i}`} />
            <div>
              <Label className="text-xs text-gray-500">Linked template (preferred)</Label>
              <TemplatePickerSelect
                value={item.template_id}
                onChange={(v) => updateItem(i, { template_id: v })}
                templates={templates}
                testid={`starter-template-${i}`}
              />
            </div>
            <Input placeholder="Optional link (used only if no template is selected)" value={item.link || ''} onChange={(e) => updateItem(i, { link: e.target.value })} />
          </div>
        ))}
        <Button variant="outline" onClick={() => setData({ ...data, items: [...data.items, { id: `se-${Date.now()}`, title: '', template_id: null, link: null }] })}>
          <Plus className="w-4 h-4 mr-2" />Add essential
        </Button>
      </div>
      {flash && (
        <div className={`mt-4 p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
      )}
      <div className="mt-5">
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-starter-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save starter essentials</>}
        </Button>
      </div>
    </section>
  );
};

const StatTile = ({ label, value }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

const StatsTab = ({ authHeader }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/admin/stats`, { headers: authHeader() });
      setStats(data);
    })();
  }, [authHeader]);
  if (!stats) return <div className="p-6 text-gray-400">Loading...</div>;
  const tierCounts = stats.subscribers_by_tier || { standard: 0, pro: 0, elite: 0 };
  const mrr = stats.estimated_mrr ?? 0;
  const assessments = stats.assessments || { total: 0, this_week: 0, this_month: 0, conversions: 0 };
  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total users" value={stats.total_users} />
        <div
          className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
          data-testid="stat-active-subscribers"
        >
          <div className="text-xs uppercase tracking-wide text-gray-500">Active subscribers</div>
          <div className="text-3xl font-bold text-gray-900 mt-1" data-testid="stat-subscribers-total">
            {stats.subscribed_users}
          </div>
          <div className="mt-2 text-sm text-gray-700 flex flex-wrap gap-x-3 gap-y-1">
            <span data-testid="stat-tier-standard">
              <span className="text-gray-500">Standard:</span>{' '}
              <span className="font-semibold text-gray-900">{tierCounts.standard}</span>
            </span>
            <span className="text-gray-300">·</span>
            <span data-testid="stat-tier-pro">
              <span className="text-gray-500">Pro:</span>{' '}
              <span className="font-semibold text-gray-900">{tierCounts.pro}</span>
            </span>
            <span className="text-gray-300">·</span>
            <span data-testid="stat-tier-elite">
              <span className="text-gray-500">Elite:</span>{' '}
              <span className="font-semibold text-gray-900">{tierCounts.elite}</span>
            </span>
          </div>
        </div>
        <div
          className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
          data-testid="stat-mrr"
        >
          <div className="text-xs uppercase tracking-wide text-gray-500">Estimated MRR</div>
          <div className="text-3xl font-bold text-gray-900 mt-1" data-testid="stat-mrr-value">
            ${mrr.toLocaleString('en-US')}
          </div>
          <div className="mt-2 text-xs text-gray-500 leading-snug">
            $39 × {tierCounts.standard} + $79 × {tierCounts.pro} + $149 × {tierCounts.elite}
          </div>
        </div>
        <StatTile label="Total templates" value={stats.total_templates} />
      </div>
      <div
        className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
        data-testid="stat-assessments"
      >
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-gray-500">HR Risk Assessment leads</div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/admin/assessment-leads/export`, {
                    headers: authHeader(),
                    responseType: 'blob',
                  });
                  const cd = res.headers['content-disposition'] || '';
                  const match = cd.match(/filename="?([^";]+)"?/);
                  const filename = match ? match[1] : `drivehr-assessment-leads-${Date.now()}.csv`;
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  alert('Export failed. Please try again.');
                }
              }}
              className="text-xs text-blue-600 hover:underline"
              data-testid="stat-assessments-export-csv"
            >
              Export CSV
            </button>
            <a
              href="/assessment"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
              data-testid="stat-assessments-page-link"
            >
              View public page →
            </a>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-assessments-week">
              {assessments.this_week}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">This week</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-assessments-month">
              {assessments.this_month}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">Last 30 days</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-assessments-total">
              {assessments.total}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">Total leads</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-assessments-conversions">
              {assessments.conversions}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">Converted to member</div>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500 leading-snug">
          Leads captured via the public HR Risk Assessment. Full lead detail lives in MailerLite (Subscribers → HR Risk Assessment group).
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold mb-3">Templates by category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(stats.templates_by_category).map(([cat, count]) => (
            <div key={cat} className="flex justify-between p-2 border-b border-gray-100 last:border-0">
              <span className="text-sm text-gray-700">{CATEGORIES.find((c) => c.id === cat)?.label || cat}</span>
              <span className="text-sm font-medium text-gray-900">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const InboxTab = ({ authHeader, onAfterStatusChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await axios.get(`${API}/admin/support-requests?${params}`, { headers: authHeader() });
      setRequests(data.requests);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterStatus]);

  const saveUpdate = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/admin/support-requests/${editing.id}`, {
        status: editing.status,
        admin_notes: editing.admin_notes,
      }, { headers: authHeader() });
      setEditing(null);
      setFlash({ type: 'success', msg: 'Request updated.' });
      await load();
      if (onAfterStatusChange) onAfterStatusChange();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const STATUS_COLORS = {
    new: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="space-y-4" data-testid="admin-inbox">
      {flash && (
        <div className={`p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
      )}
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label htmlFor="inb-type">Type</Label>
            <select id="inb-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm">
              <option value="">All</option>
              <option value="ask_drivehr">Ask DriveHR</option>
              <option value="document_review">Document Review</option>
            </select>
          </div>
          <div>
            <Label htmlFor="inb-status">Status</Label>
            <select id="inb-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm">
              <option value="">All</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3">Submitted</th>
                <th className="p-3">From</th>
                <th className="p-3">Type</th>
                <th className="p-3">Subject</th>
                <th className="p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-gray-400">Loading...</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-gray-400">No requests yet.</td></tr>
              ) : requests.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{r.user_name || r.user_email}</div>
                    <div className="text-xs text-gray-500">{r.user_email}{r.user_tier && <> · <span className="capitalize">{r.user_tier}</span></>}</div>
                  </td>
                  <td className="p-3 text-xs">{r.type === 'ask_drivehr' ? 'Ask DriveHR' : 'Document Review'}</td>
                  <td className="p-3 text-gray-900">{r.subject}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[r.status] || ''}`}>{r.status}</span></td>
                  <td className="p-3"><Button size="sm" variant="outline" onClick={() => setEditing({ ...r, admin_notes: r.admin_notes || '' })} data-testid={`inbox-open-${r.id}`}>Open</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">{editing.type === 'ask_drivehr' ? 'Ask DriveHR Request' : 'Document Review Request'}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-gray-500">From</div>
                <div className="font-medium">{editing.user_name} · {editing.user_email}{editing.user_tier && <> · <span className="capitalize">{editing.user_tier}</span></>}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Subject</div>
                <div className="font-medium">{editing.subject}</div>
              </div>
              {editing.type === 'ask_drivehr' && (
                <div>
                  <div className="text-xs text-gray-500">Question</div>
                  <div className="whitespace-pre-line p-3 bg-gray-50 rounded">{editing.payload?.question}</div>
                </div>
              )}
              {editing.type === 'document_review' && (
                <>
                  {editing.payload?.file_link && (
                    <div><div className="text-xs text-gray-500">Document link</div><a href={editing.payload.file_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{editing.payload.file_link}</a></div>
                  )}
                  {editing.payload?.notes && (
                    <div><div className="text-xs text-gray-500">Notes</div><div className="whitespace-pre-line p-3 bg-gray-50 rounded">{editing.payload.notes}</div></div>
                  )}
                </>
              )}
              <div className="pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="inbox-status-select">
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
              <div><Label>Admin notes (internal)</Label><Textarea rows={4} value={editing.admin_notes} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} data-testid="inbox-admin-notes" /></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveUpdate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="inbox-save"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComplianceAlertsAdminTab = ({ authHeader }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('info');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portal/admin/compliance-alerts`, { headers: authHeader() });
      setAlerts(data.alerts);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setFlash(null);
    try {
      const payload = { title, body, severity };
      if (link.trim()) payload.link = link.trim();
      await axios.post(`${API}/portal/admin/compliance-alerts`, payload, { headers: authHeader() });
      setTitle(''); setBody(''); setSeverity('info'); setLink('');
      setFlash({ type: 'success', msg: 'Alert published.' });
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Archive this alert?')) return;
    await axios.delete(`${API}/portal/admin/compliance-alerts/${id}`, { headers: authHeader() });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-alerts">
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900 mb-1">New compliance alert</h2>
        <p className="text-sm text-gray-500 mb-4">Visible immediately to Pro and Elite members on their Compliance Alerts page.</p>
        <form onSubmit={create} className="space-y-3">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="alert-title" /></div>
          <div><Label>Body</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} required data-testid="alert-body" /></div>
          <div>
            <Label>Severity</Label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="alert-severity">
              <option value="info">Info (blue)</option>
              <option value="warn">Warn (amber)</option>
              <option value="critical">Critical (red)</option>
            </select>
          </div>
          <div>
            <Label htmlFor="alert-link">Link <span className="text-xs font-normal text-gray-400">(optional — opens in new tab when members click the alert)</span></Label>
            <Input id="alert-link" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://example.com/source-article" data-testid="alert-link" />
          </div>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="publish-alert-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publishing...</> : <><Plus className="w-4 h-4 mr-2" />Publish alert</>}
          </Button>
          {flash && (
            <div className={`p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
          )}
        </form>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Active alerts</h3>
        {loading ? <div className="text-gray-400">Loading...</div> : alerts.length === 0 ? (
          <p className="text-gray-500 text-sm">No active alerts.</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 p-3 border border-gray-100 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{a.title}</h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : a.severity === 'warn' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{a.severity}</span>
                    {a.link && (
                      <a href={a.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5" data-testid={`admin-alert-link-${a.id}`}>
                        <LinkIcon className="w-3 h-3" />link
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{a.body}</p>
                  <div className="text-xs text-gray-400 mt-1">{new Date(a.published_at).toLocaleDateString()}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(a.id)} className="text-red-600 hover:text-red-700" data-testid={`archive-alert-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const MembersSortHeader = ({ k, sortKey, sortDir, onToggle, children }) => (
  <th
    onClick={() => onToggle(k)}
    className="p-3 cursor-pointer select-none hover:text-gray-900"
    data-testid={`members-sort-${k}`}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {sortKey === k && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </span>
  </th>
);

// Contact form inquiries — prospect/sales leads from the public Contact form.
// Distinct from the member-portal Inbox (Ask DriveHR / Document Review) so
// sales pipeline can be tracked independently.
const ContactInquiriesTab = ({ authHeader }) => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/contacts`, { headers: authHeader() });
        setData(d);
      } catch (e) {
        console.error('Failed to load contact inquiries', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeader]);

  return (
    <div className="space-y-4" data-testid="admin-leads">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Contact Inquiries</h2>
        <span className="text-sm text-gray-500">{data.total} total</span>
      </div>
      <p className="text-sm text-gray-500">
        Prospect and sales inquiries from the public Contact form. Member support requests appear under the Inbox tab.
      </p>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : data.items.length === 0 ? (
        <div className="text-gray-400 py-8 text-center border border-dashed rounded-lg" data-testid="leads-empty">
          No contact inquiries yet. New submissions from the public Contact form will appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((c, idx) => (
            <div key={c.id || idx} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`lead-row-${idx}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">{c.name}</div>
                  <div className="text-sm text-gray-600">
                    <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline">{c.email}</a>
                    {c.company && <> &nbsp;&middot;&nbsp; <span className="text-gray-700">{c.company}</span></>}
                  </div>
                </div>
                <span className="text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
              </div>
              {c.message && (
                <p className="mt-3 text-sm text-gray-700 whitespace-pre-line">{c.message}</p>
              )}
              {(c.phone || c.subject) && (
                <div className="mt-2 text-xs text-gray-500">
                  {c.subject && <>Subject: <span className="text-gray-700">{c.subject}</span></>}
                  {c.subject && c.phone && ' · '}
                  {c.phone && <>Phone: <span className="text-gray-700">{c.phone}</span></>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



const MembersTab = ({ authHeader }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [flash, setFlash] = useState(null);
  const [resetting, setResetting] = useState({}); // user_id -> true
  const [resetResult, setResetResult] = useState(null); // { email, temp_password }
  const [copied, setCopied] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null); // member object

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (filterTier) params.set('tier', filterTier);
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await axios.get(`${API}/admin/members?${params}`, { headers: authHeader() });
      setMembers(data.members);
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTier, filterStatus]);

  const sortedMembers = useMemo(() => {
    const arr = [...members];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [members, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const doReset = async (m) => {
    setResetting((s) => ({ ...s, [m.id]: true }));
    setFlash(null);
    try {
      const { data } = await axios.post(`${API}/admin/members/${m.id}/reset-password`, {}, { headers: authHeader() });
      setResetResult(data);
      setCopied(false);
      setConfirmReset(null);
      await load();
    } catch (e) {
      setFlash({ type: 'error', msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      setConfirmReset(null);
    } finally {
      setResetting((s) => ({ ...s, [m.id]: false }));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // fallback: select text
      setCopied(false);
    }
  };

  const TIER_COLORS = {
    standard: 'bg-blue-100 text-blue-700',
    pro: 'bg-amber-100 text-amber-700',
    elite: 'bg-purple-100 text-purple-700',
  };
  const STATUS_COLORS = {
    active: 'bg-green-100 text-green-700',
    canceled: 'bg-gray-100 text-gray-600',
    past_due: 'bg-red-100 text-red-700',
    trialing: 'bg-blue-100 text-blue-700',
    incomplete: 'bg-amber-100 text-amber-700',
  };

  // SortHeader is a thin wrapper around MembersSortHeader to capture sort state.

  return (
    <div className="space-y-4" data-testid="admin-members">
      {flash && (
        <div className={`p-3 rounded-lg border text-sm ${flash.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{flash.msg}</div>
      )}
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-gray-900">Members</h2>
          <span className="text-sm text-gray-500" data-testid="members-total">{members.length} member{members.length === 1 ? '' : 's'}</span>
        </div>
        <p className="text-sm text-gray-500 mb-4">Search, sort, and manage member accounts. Use &ldquo;Reset password&rdquo; to force-revoke their sessions and generate a one-time temporary password.</p>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[220px]">
            <Label htmlFor="mem-search">Search</Label>
            <Input id="mem-search" placeholder="Name or email..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="members-search-input" />
          </div>
          <div>
            <Label htmlFor="mem-tier">Tier</Label>
            <select id="mem-tier" value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="members-tier-filter">
              <option value="">All</option>
              <option value="standard">Standard</option>
              <option value="pro">Pro</option>
              <option value="elite">Elite</option>
            </select>
          </div>
          <div>
            <Label htmlFor="mem-status">Status</Label>
            <select id="mem-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm" data-testid="members-status-filter">
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="canceled">Canceled</option>
              <option value="past_due">Past due</option>
              <option value="trialing">Trialing</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="members-search-btn">Search</Button>
        </form>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left bg-gray-50 text-gray-600">
              <tr>
                <MembersSortHeader k="first_name" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Name</MembersSortHeader>
                <MembersSortHeader k="email" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Email</MembersSortHeader>
                <MembersSortHeader k="membership_tier" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Tier</MembersSortHeader>
                <MembersSortHeader k="membership_status" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Status</MembersSortHeader>
                <MembersSortHeader k="created_at" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Joined</MembersSortHeader>
                <MembersSortHeader k="downloads_this_month" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>DL / mo</MembersSortHeader>
                <th className="p-3">Stripe</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-400">Loading...</td></tr>
              ) : sortedMembers.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-gray-400">No members match those filters.</td></tr>
              ) : sortedMembers.map((m) => (
                <tr key={m.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`member-row-${m.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{m.first_name} {m.last_name}</div>
                    {m.role === 'admin' && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">admin</span>}
                    {m.password_reset_required && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-800" title="Member must change password on next login">temp pw</span>}
                  </td>
                  <td className="p-3 text-gray-700">{m.email}</td>
                  <td className="p-3">{m.membership_tier ? <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${TIER_COLORS[m.membership_tier] || ''}`}>{m.membership_tier}</span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="p-3">{m.membership_status ? <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_COLORS[m.membership_status] || ''}`}>{m.membership_status}</span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-center text-gray-700" data-testid={`member-dl-${m.id}`}>{m.downloads_this_month}</td>
                  <td className="p-3 text-xs">{m.stripe_customer_id ? <code className="text-gray-600">{m.stripe_customer_id.slice(0, 14)}…</code> : <span className="text-gray-400">—</span>}</td>
                  <td className="p-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!!resetting[m.id] || m.role === 'admin'}
                      onClick={() => setConfirmReset(m)}
                      data-testid={`member-reset-${m.id}`}
                      title={m.role === 'admin' ? 'Admins use Account tab to change own password' : 'Force-reset this member\'s password'}
                    >
                      {resetting[m.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {confirmReset && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmReset(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="members-confirm-modal">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Reset password for {confirmReset.first_name} {confirmReset.last_name}?</h3>
                <p className="text-sm text-gray-600 mt-2">
                  This will generate a temporary password and <span className="font-semibold">sign this member out of every device</span>. The temp password will be shown <span className="font-semibold">once</span> — copy it and share securely.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setConfirmReset(null)} data-testid="members-cancel-reset">Cancel</Button>
              <Button onClick={() => doReset(confirmReset)} disabled={resetting[confirmReset.id]} className="bg-red-600 hover:bg-red-700 text-white" data-testid="members-confirm-reset">
                {resetting[confirmReset.id] ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</> : 'Reset password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {resetResult && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setResetResult(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="members-reset-result">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Temporary password generated</h3>
                <p className="text-sm text-gray-600 mt-1">For <span className="font-semibold">{resetResult.email}</span></p>
              </div>
            </div>
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 font-mono text-lg text-center text-gray-900 break-all" data-testid="members-temp-password">
              {resetResult.temp_password}
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
              {resetResult.message}
            </p>
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 mt-2" data-testid="members-reset-forced-note">
              On their next sign-in they&apos;ll be required to set a new permanent password before they can access the portal.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => copyToClipboard(resetResult.temp_password)} data-testid="members-copy-temp">
                {copied ? <><CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />Copied</> : 'Copy password'}
              </Button>
              <Button onClick={() => setResetResult(null)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="members-close-reset">Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AccountTab = () => (
  <div className="space-y-6 max-w-3xl" data-testid="admin-account">
    <UpdateProfileForm />
    <ChangePasswordForm />
  </div>
);

export const AdminPanel = () => {
  const { user, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('templates');
  const [pendingInbox, setPendingInbox] = useState(0);

  const refreshInboxCount = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/support-requests/pending-count`, { headers: authHeader() });
      setPendingInbox(data.pending || 0);
    } catch (_) { /* silent — badge is best-effort */ }
  }, [authHeader]);

  useEffect(() => { refreshInboxCount(); }, [refreshInboxCount]);

  // Refresh count whenever the admin switches tabs (cheap, ensures freshness
  // after they edit a request without us needing to thread a callback through
  // every child).
  useEffect(() => { refreshInboxCount(); }, [tab, refreshInboxCount]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xl font-bold" style={{ color: '#1A56E8' }}>DriveHR</div>
            <span className="text-gray-300">|</span>
            <span className="text-gray-700 font-medium">Admin</span>
            <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">{user?.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/members')} data-testid="admin-member-dashboard-btn"><LayoutDashboard className="w-4 h-4 mr-1" />Member Dashboard</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="admin-public-site-btn"><Globe className="w-4 h-4 mr-1" />Public Site</Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-4 h-4 mr-1" />Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
          <Tab active={tab === 'stats'} onClick={() => setTab('stats')} testid="tab-stats">Overview</Tab>
          <Tab active={tab === 'templates'} onClick={() => setTab('templates')} testid="tab-templates">Templates</Tab>
          <Tab active={tab === 'inbox'} onClick={() => setTab('inbox')} testid="tab-inbox" badgeCount={pendingInbox}>Inbox</Tab>
          <Tab active={tab === 'members'} onClick={() => setTab('members')} testid="tab-members">Members</Tab>
          <Tab active={tab === 'alerts'} onClick={() => setTab('alerts')} testid="tab-alerts">Compliance Alerts</Tab>
          <Tab active={tab === 'reminders'} onClick={() => setTab('reminders')} testid="tab-reminders">Monthly Reminders</Tab>
          <Tab active={tab === 'tools'} onClick={() => setTab('tools')} testid="tab-tools">Featured Tools</Tab>
          <Tab active={tab === 'starter'} onClick={() => setTab('starter')} testid="tab-starter">Starter Essentials</Tab>
          <Tab active={tab === 'account'} onClick={() => setTab('account')} testid="tab-account">Account</Tab>
        </div>
        {tab === 'stats' && <StatsTab authHeader={authHeader} />}
        {tab === 'templates' && <TemplatesTab authHeader={authHeader} />}
        {tab === 'inbox' && <InboxTab authHeader={authHeader} onAfterStatusChange={refreshInboxCount} />}
        {tab === 'leads' && <ContactInquiriesTab authHeader={authHeader} />}
        {tab === 'members' && <MembersTab authHeader={authHeader} />}
        {tab === 'alerts' && <ComplianceAlertsAdminTab authHeader={authHeader} />}
        {tab === 'reminders' && <RemindersTab authHeader={authHeader} />}
        {tab === 'tools' && <FeaturedToolsTab authHeader={authHeader} />}
        {tab === 'starter' && <StarterEssentialsTab authHeader={authHeader} />}
        {tab === 'account' && <AccountTab />}
      </main>
    </div>
  );
};
